import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  ToggleLeft, ToggleRight, Building2, Zap, Brain, 
  Mail, MessageSquare, TestTube, BarChart3, FileText, Receipt,
  Key, Save, Search, RefreshCw, Users, Calendar,
  ClipboardList, Pill, BedDouble, Activity, FileCheck, Bell,
  Stethoscope, UserCog, FlaskConical, Phone, MessageCircle,
  LayoutDashboard, Settings, Shield, Network, Workflow, Send,
  UsersRound, Lock, KeyRound, Megaphone, Paintbrush
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import platformApi from '@/lib/platformApi';

// Core Modules - Essential hospital operations
const CORE_MODULES = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, description: 'Main analytics dashboard' },
  { key: 'patients', label: 'Patients', icon: Users, description: 'Patient registration and management' },
  { key: 'visits', label: 'Visit Management', icon: Activity, description: 'Patient visits and consultations' },
  { key: 'appointments', label: 'Appointments', icon: Calendar, description: 'Appointment scheduling' },
  { key: 'admissions', label: 'Admissions', icon: BedDouble, description: 'Patient admission management' },
  { key: 'medical_records', label: 'Medical Records', icon: FileCheck, description: 'Patient medical records' },
  { key: 'prescriptions', label: 'Prescriptions', icon: Pill, description: 'Prescription management' },
  { key: 'billing', label: 'Billing', icon: Receipt, description: 'Billing and invoicing' },
  { key: 'fixed_charges', label: 'Fixed Charges Master', icon: Receipt, description: 'Fixed charge management' },
  { key: 'discharge_summary', label: 'Discharge Summary', icon: FileText, description: 'Discharge documentation' },
  { key: 'notifications', label: 'Notifications', icon: Bell, description: 'In-app notification center' },
];

// Lab & Diagnostics
const LAB_MODULES = [
  { key: 'lab_entries', label: 'Lab Entries', icon: TestTube, description: 'Lab test entries and results' },
  { key: 'lab_orders', label: 'Lab Orders', icon: FlaskConical, description: 'Lab order management and tracking' },
  { key: 'lab_integration', label: 'External Lab Integration', icon: Network, description: 'External lab system integration' },
];

// Communication & Messaging
const COMMUNICATION_MODULES = [
  { key: 'communication_logs', label: 'Communication Logs', icon: MessageCircle, description: 'View all sent communications' },
  { key: 'message_templates', label: 'Message Templates', icon: MessageSquare, description: 'WhatsApp/SMS templates' },
  { key: 'sms_notifications', label: 'SMS Notifications', icon: Phone, description: 'Send SMS to patients' },
  { key: 'whatsapp_notifications', label: 'WhatsApp Notifications', icon: MessageCircle, description: 'Send WhatsApp messages' },
  { key: 'email_notifications', label: 'Email Notifications', icon: Mail, description: 'Send emails to patients' },
  { key: 'bulk_messaging', label: 'Bulk Messaging', icon: Send, description: 'Send bulk messages to recipients' },
  { key: 'marketing_messages', label: 'Marketing Messages', icon: Megaphone, description: 'Marketing and promotional campaigns' },
  { key: 'community_workers', label: 'Community Workers', icon: UsersRound, description: 'Community health worker management' },
];

// Referral Management
const REFERRAL_MODULES = [
  { key: 'referring_doctors', label: 'Referring Doctors', icon: Stethoscope, description: 'External referring doctor management' },
  { key: 'referral_analytics', label: 'Referral Analytics', icon: BarChart3, description: 'Referral tracking and analytics' },
  { key: 'referral_triage', label: 'Referral Triage', icon: Workflow, description: 'Manage incoming referrals' },
];

// Administration & Settings
const ADMIN_MODULES = [
  { key: 'user_management', label: 'User Management', icon: UserCog, description: 'Staff and user accounts' },
  { key: 'provider_management', label: 'Provider Management', icon: Stethoscope, description: 'Manage healthcare providers' },
  { key: 'audit_logs', label: 'Audit History', icon: ClipboardList, description: 'Activity tracking and compliance' },
  { key: 'security_settings', label: 'Security Settings', icon: Shield, description: 'Security and access controls' },
];

// Account & Security Features
const SECURITY_MODULES = [
  { key: 'password_reset', label: 'Password Reset', icon: KeyRound, description: 'Self-service password reset via email' },
  { key: 'account_lockout', label: 'Account Lockout', icon: Lock, description: 'Account lockout after failed attempts' },
  { key: 'account_unlock', label: 'Account Unlock', icon: Key, description: 'Self-service account unlock via email' },
  { key: 'admin_password_reset', label: 'Admin Password Reset', icon: UserCog, description: 'Admin can reset user passwords' },
];

// Premium/AI Features
const PREMIUM_FEATURES = [
  { key: 'ai_scribe', label: 'AI Scribe', icon: Brain, description: 'AI-powered medical transcription' },
  { key: 'ai_summary', label: 'AI Summary', icon: Brain, description: 'AI clinical summaries and SOAP notes' },
  { key: 'discharge_analytics', label: 'Discharge Analytics', icon: BarChart3, description: 'Advanced discharge analytics' },
  { key: 'custom_branding', label: 'Custom Branding', icon: Paintbrush, description: 'White-label customization' },
];

// Portal Access
const PORTAL_MODULES = [
  { key: 'patient_portal', label: 'Patient Portal', icon: Users, description: 'Patient self-service portal' },
  { key: 'provider_portal', label: 'Provider Portal', icon: Stethoscope, description: 'Provider/Doctor portal access' },
];

export default function FeatureFlags() {
  const [hospitals, setHospitals] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchHospitals();
  }, []);

  const fetchHospitals = async () => {
    try {
      const response = await platformApi.get('/platform/hospitals');
      setHospitals(response.data);
      if (response.data.length > 0) {
        selectHospital(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load hospitals');
    } finally {
      setLoading(false);
    }
  };

  const selectHospital = async (hospital) => {
    try {
      const response = await platformApi.get(`/platform/hospitals/${hospital.id}/features`);
      setSelectedHospital(hospital);
      setFeatures(response.data.features || {});
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to load features');
    }
  };

  const toggleFeature = (key) => {
    setFeatures(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setHasChanges(true);
  };

  const saveFeatures = async () => {
    if (!selectedHospital) return;
    setSaving(true);

    try {
      await platformApi.put(`/platform/hospitals/${selectedHospital.id}/features`, features);
      toast.success('Features updated successfully');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save features');
    } finally {
      setSaving(false);
    }
  };

  const enableAll = () => {
    const allEnabled = {};
    [...CORE_MODULES, ...LAB_MODULES, ...COMMUNICATION_MODULES, ...REFERRAL_MODULES, ...ADMIN_MODULES, ...SECURITY_MODULES, ...PREMIUM_FEATURES, ...PORTAL_MODULES].forEach(f => { allEnabled[f.key] = true; });
    setFeatures(allEnabled);
    setHasChanges(true);
  };

  const disableAll = () => {
    const allDisabled = {};
    [...CORE_MODULES, ...LAB_MODULES, ...COMMUNICATION_MODULES, ...REFERRAL_MODULES, ...ADMIN_MODULES, ...SECURITY_MODULES, ...PREMIUM_FEATURES, ...PORTAL_MODULES].forEach(f => { allDisabled[f.key] = false; });
    setFeatures(allDisabled);
    setHasChanges(true);
  };

  const filteredHospitals = hospitals.filter(h => 
    h.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    h.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderFeatureToggle = (feature) => {
    const isEnabled = features[feature.key] || false;
    return (
      <div 
        key={feature.key}
        className={`p-3 rounded-lg border transition-colors ${
          isEnabled 
            ? 'bg-emerald-500/10 border-emerald-500/30' 
            : 'bg-slate-700/30 border-slate-600/50'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isEnabled ? 'bg-emerald-500/20' : 'bg-slate-600/50'}`}>
              <feature.icon className={`h-4 w-4 ${isEnabled ? 'text-emerald-400' : 'text-slate-400'}`} />
            </div>
            <div>
              <p className="text-white font-medium text-sm">{feature.label}</p>
              <p className="text-xs text-slate-400">{feature.description}</p>
            </div>
          </div>
          <button
            onClick={() => toggleFeature(feature.key)}
            className="focus:outline-none"
            data-testid={`toggle-${feature.key}`}
          >
            {isEnabled ? (
              <ToggleRight className="h-7 w-7 text-emerald-400" />
            ) : (
              <ToggleLeft className="h-7 w-7 text-slate-500" />
            )}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Feature Flags</h1>
        <p className="text-slate-400">Enable or disable features for individual hospitals</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Hospital Selector */}
        <Card className="bg-slate-800/50 border-slate-700 lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-400" />
              Select Hospital
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search hospitals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                data-testid="search-hospital-features"
              />
            </div>

            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filteredHospitals.map((hospital) => (
                <button
                  key={hospital.id}
                  onClick={() => selectHospital(hospital)}
                  className={`w-full p-3 rounded-lg text-left transition-colors ${
                    selectedHospital?.id === hospital.id
                      ? 'bg-emerald-600/20 border border-emerald-500/30'
                      : 'bg-slate-700/30 hover:bg-slate-700/50 border border-transparent'
                  }`}
                  data-testid={`select-hospital-${hospital.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                      {hospital.name?.charAt(0) || 'H'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{hospital.name}</p>
                      <p className="text-xs text-slate-400">{hospital.subscription_tier}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Feature Toggles */}
        <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-400" />
                {selectedHospital?.name || 'Select a Hospital'}
              </CardTitle>
              {selectedHospital && (
                <p className="text-xs text-slate-400 mt-1">
                  Tier: {selectedHospital.subscription_tier} • Status: {selectedHospital.subscription_status}
                </p>
              )}
            </div>
            {selectedHospital && (
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={enableAll}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700 text-xs"
                >
                  Enable All
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={disableAll}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700 text-xs"
                >
                  Disable All
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {selectedHospital ? (
              <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
                {/* Core Modules Section */}
                <div>
                  <h3 className="text-sm font-semibold text-blue-400 mb-3 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Core Modules
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {CORE_MODULES.map(renderFeatureToggle)}
                  </div>
                </div>

                {/* Lab & Diagnostics Section */}
                <div>
                  <h3 className="text-sm font-semibold text-purple-400 mb-3 flex items-center gap-2">
                    <FlaskConical className="h-4 w-4" />
                    Lab & Diagnostics
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {LAB_MODULES.map(renderFeatureToggle)}
                  </div>
                </div>

                {/* Communication Section */}
                <div>
                  <h3 className="text-sm font-semibold text-green-400 mb-3 flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Communication & Messaging
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {COMMUNICATION_MODULES.map(renderFeatureToggle)}
                  </div>
                </div>

                {/* Referral Management Section */}
                <div>
                  <h3 className="text-sm font-semibold text-cyan-400 mb-3 flex items-center gap-2">
                    <Stethoscope className="h-4 w-4" />
                    Referral Management
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {REFERRAL_MODULES.map(renderFeatureToggle)}
                  </div>
                </div>

                {/* Administration Section */}
                <div>
                  <h3 className="text-sm font-semibold text-orange-400 mb-3 flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Administration
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {ADMIN_MODULES.map(renderFeatureToggle)}
                  </div>
                </div>

                {/* Security & Account Section */}
                <div>
                  <h3 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Security & Account
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {SECURITY_MODULES.map(renderFeatureToggle)}
                  </div>
                </div>

                {/* Portal Access Section */}
                <div>
                  <h3 className="text-sm font-semibold text-indigo-400 mb-3 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Portal Access
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {PORTAL_MODULES.map(renderFeatureToggle)}
                  </div>
                </div>

                {/* Premium Features Section */}
                <div>
                  <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Premium Features
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {PREMIUM_FEATURES.map(renderFeatureToggle)}
                  </div>
                </div>

                {hasChanges && (
                  <div className="flex justify-end pt-4 border-t border-slate-700 sticky bottom-0 bg-slate-800/90 backdrop-blur-sm py-3">
                    <Button 
                      onClick={saveFeatures}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      disabled={saving}
                      data-testid="save-features-btn"
                    >
                      {saving ? (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-12 text-center text-slate-500">
                <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a hospital to manage its features</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
